import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { AriaOrb } from "../components/AriaOrb";
import { FocusTimer } from "../components/FocusTimer";
import { Timer, Loader2, RefreshCw, CheckCircle2, Circle, Mic2, BookOpen, Youtube, StickyNote, FileText, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SOURCE_ICON = { video: Youtube, note: StickyNote, turbolearn: BookOpen, paper: FileText };
const SOURCE_ROUTE = { video: "/app/learn", note: "/app/notes", turbolearn: "/app/turbolearn", paper: "/app/documents" };

export function usePlan() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/plan"); setPlan(data.plan); } catch (err) { console.error("[ARIA]", err); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try { const { data } = await api.post("/plan/generate", {}); setPlan(data.plan); toast.success("Countdown plan ready"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not build a plan"); }
    finally { setGenerating(false); }
  };

  const toggle = async (taskId) => {
    try { const { data } = await api.post(`/plan/tasks/${taskId}/toggle`); setPlan(data.plan); } catch (err) { console.error("[ARIA]", err); }
  };

  return { plan, loading, generating, generate, toggle, reload: load };
}

export function planProgress(plan) {
  const tasks = (plan?.days || []).flatMap(d => d.tasks);
  const done = tasks.filter(t => t.done).length;
  return { total: tasks.length, done, pct: tasks.length ? Math.round((done / tasks.length) * 100) : 0 };
}

export default function Plan() {
  const navigate = useNavigate();
  const { plan, loading, generating, generate, toggle } = usePlan();
  const [starting, setStarting] = useState(null);

  const startDrill = async (task) => {
    setStarting(task.id);
    try {
      const body = task.type === "mock"
        ? { target: "Full mock interview — final rehearsal", topic_focus: [] }
        : { target: `Review: ${task.concept}`, topic_focus: [task.concept] };
      const { data } = await api.post("/interviews", body);
      navigate(`/app/interviews/${data.interview_id}`);
    } catch { toast.error("Could not start"); setStarting(null); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const prog = planProgress(plan);

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1"><Timer size={22} className="text-[var(--aria-primary)]" /><h1 className="font-display text-3xl">Countdown</h1></div>
            <p className="text-sm text-[var(--aria-text-muted)]">A day-by-day drill plan that works backwards from your next interview to close every weak concept.</p>
          </div>
          <button onClick={generate} disabled={generating} data-testid="plan-generate-button"
            className="btn btn-primary px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {plan ? "Rebuild plan" : "Build my plan"}
          </button>
        </div>

        {loading && <div className="text-sm text-[var(--aria-text-muted)]">Loading…</div>}

        {!loading && !plan && (
          <div className="aria-card p-10 text-center" data-testid="plan-empty">
            <AriaOrb size={56} state={generating ? "thinking" : "idle"} className="mx-auto mb-4" />
            <div className="font-display text-xl mb-2">No countdown yet</div>
            <p className="text-sm text-[var(--aria-text-muted)] max-w-md mx-auto">
              ARIA will take your next interview or exam from the Calendar, find every level-1 concept, order them prerequisites-first and spread them across the days you have left.
            </p>
          </div>
        )}

        {plan && (
          <>
            <div className="aria-card aria-card-accent p-6 mb-6 relative overflow-hidden" data-testid="plan-header">
              <div className="absolute inset-0 bg-grad-soft opacity-40 pointer-events-none" />
              <div className="relative flex items-start gap-5 flex-wrap">
                <div className="text-center px-4">
                  <div className="font-display text-5xl leading-none text-[var(--aria-primary)]" data-testid="plan-days-left">{plan.days_left}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--aria-text-muted)] mt-1">days left</div>
                </div>
                <div className="flex-1 min-w-[240px]">
                  <div className="text-xs uppercase tracking-[0.2em] text-[var(--aria-primary)] font-semibold mb-1">{plan.deadline_type} · {new Date(plan.due_date).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
                  <div className="font-display text-xl mb-2" data-testid="plan-target">{plan.deadline_title}</div>
                  <p className="text-sm leading-relaxed text-[var(--aria-text-muted)]" data-testid="plan-strategy">{plan.strategy}</p>
                  <div className="mt-4">
                    <div className="flex justify-between text-[11px] text-[var(--aria-text-muted)] mb-1"><span>{prog.done}/{prog.total} tasks done</span><span>{prog.pct}%</span></div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-grad-brand transition-[width] duration-500" style={{ width: `${prog.pct}%` }} data-testid="plan-progress-bar" /></div>
                  </div>
                </div>
              </div>
              <div className="relative mt-4 flex flex-wrap gap-2" data-testid="plan-concepts">
                {plan.concepts.map(c => <span key={c.concept} className="aria-pill text-xs">{c.concept} · L{c.level}</span>)}
              </div>
            </div>

            <div className="relative pl-6">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-[var(--aria-primary)] via-[var(--aria-border)] to-transparent" />
              <div className="space-y-5">
                {plan.days.map((d, di) => {
                  const isToday = d.date === today;
                  const past = d.date < today;
                  const allDone = d.tasks.every(t => t.done);
                  return (
                    <div key={d.date} className="relative" data-testid={`plan-day-${di}`}>
                      <span className={`absolute -left-6 top-4 w-[15px] h-[15px] rounded-full border-2 ${allDone ? "bg-emerald-400 border-emerald-400" : isToday ? "bg-[var(--aria-primary)] border-[var(--aria-primary)] shadow-[0_0_14px_var(--aria-primary)]" : "bg-[var(--aria-bg)] border-[var(--aria-border)]"}`} />
                      <div className={`aria-card p-5 ${isToday ? "ring-glow-primary" : ""} ${past && !allDone ? "opacity-70" : ""}`}>
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <div className="flex items-center gap-3">
                            <span className={`font-mono text-xs px-2 py-0.5 rounded ${isToday ? "bg-[var(--aria-primary)] text-white" : "bg-white/5 text-[var(--aria-text-muted)]"}`}>{d.label}</span>
                            <span className="font-display text-lg">{d.title}</span>
                            {isToday && <span className="aria-pill aria-pill-primary text-[10px]">Today</span>}
                            {allDone && <span className="aria-pill aria-pill-success text-[10px]">Done</span>}
                          </div>
                          <span className="text-xs text-[var(--aria-text-muted)]">{new Date(d.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</span>
                        </div>
                        <div className="space-y-1.5">
                          {d.tasks.map(t => <TaskRow key={t.id} task={t} onToggle={() => toggle(t.id)} onStart={() => startDrill(t)} onOpen={() => navigate(SOURCE_ROUTE[t.source_type] || "/app/search")} starting={starting === t.id} onLogged={(_, done) => done && !t.done && toggle(t.id)} />)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TaskRow({ task, onToggle, onStart, onOpen, starting, onLogged }) {
  const Icon = task.type === "study" ? (SOURCE_ICON[task.source_type] || BookOpen) : task.type === "mock" ? Sparkles : Mic2;
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-md hover:bg-white/[0.03] ${task.done ? "opacity-60" : ""}`} data-testid={`plan-task-${task.id}`}>
      <button onClick={onToggle} data-testid={`plan-task-toggle-${task.id}`} className="shrink-0 text-[var(--aria-text-muted)] hover:text-emerald-400">
        {task.done ? <CheckCircle2 size={18} className="text-emerald-400" /> : <Circle size={18} />}
      </button>
      <Icon size={14} className="text-[var(--aria-primary)] shrink-0" />
      <div className={`flex-1 text-sm ${task.done ? "line-through" : ""}`}>{task.text}</div>
      {!task.done && (task.type === "drill" || task.type === "mock") && (
        <button onClick={onStart} disabled={starting} data-testid={`plan-task-start-${task.id}`}
          className="text-xs text-[var(--aria-primary)] hover:underline inline-flex items-center gap-1 shrink-0">
          {starting ? <Loader2 size={12} className="animate-spin" /> : null} Start <ArrowRight size={11} />
        </button>
      )}
      {task.type === "study" && <FocusTimer concept={task.concept} sourceTitle={task.source_title} taskId={task.id} onLogged={onLogged} />}
      {!task.done && task.type === "study" && task.source_type && (
        <button onClick={onOpen} data-testid={`plan-task-open-${task.id}`} className="text-xs text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] inline-flex items-center gap-1 shrink-0">
          Open <ArrowRight size={11} />
        </button>
      )}
    </div>
  );
}
